// Areas of the Virtual Tour (Advanced Virtual Tour). The project chooses how
// the scans split (backend utils/tourAreaRules.ts):
//   gaps    scans the tour rings connect — within ring reach of each other,
//           chained, are one area; a gap no ring crosses starts another;
//   file    one area per point cloud file;
//   floor   one area per storey, from the height of the floor under each scan;
//   custom  areas drawn by hand; scans in none are split by gaps.
// File and floor areas are split by gaps too, so every area can be walked by
// its rings. Automatic names come from the files or the floors until someone
// renames an area — a name is kept on one station of it, per way of splitting,
// and follows that station when the areas change; a drawn area has its own.

export interface Vec3 { x: number; y: number; z: number }
export interface TourStation { id: string; fileId: string; index: number; position: Vec3 }
export type TourAreaMode = 'gaps' | 'file' | 'floor' | 'custom';
export interface TourAreaName { stationId: string; name: string; mode?: TourAreaMode }
export interface TourAreaGroup { id: string; name: string; stationIds: string[] }

export interface TourArea {
  // The first station's id: a React key, stable while the area keeps it.
  key: string;
  // Sorted by file, then by scan number.
  stations: TourStation[];
  centre: Vec3;
  name: string;
  // A person named it (else the name is the automatic one).
  named: boolean;
  // Where a rename is saved: the named station, else the representative.
  nameStation: string;
  // The station nearest the area's middle: its photo stands for the area.
  representative: TourStation;
  colour: string;
  // A drawn area: renamed through its group, not a station.
  groupId?: string;
}

export type StationLink = (a: TourStation, b: TourStation) => boolean;

// Same reach as the rings (TOUR_MAX_DISTANCE / TOUR_MAX_Z_DELTA in SmartView).
export const AREA_HOP_M = 20;
export const AREA_MAX_DZ_M = 2;

// Fixed order, largest area first; past the eighth every area is grey.
export const AREA_COLOURS = ['#0061ff', '#0d9488', '#d97706', '#7c4dff', '#db2777', '#4d7c0f', '#0891b2', '#9a3412'];
export const AREA_OTHER_COLOUR = '#64748b';

const dist = (a: Vec3, b: Vec3) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const flatDist = (a: Vec3, b: Vec3) => Math.hypot(a.x - b.x, a.y - b.y);

// Without an occupancy grid the rings never cross storeys more than 2 m apart.
export const ringLink = (withGrid: boolean, hop = AREA_HOP_M, maxDz = AREA_MAX_DZ_M): StationLink => (a, b) => {
  if (!withGrid && Math.abs(a.position.z - b.position.z) > maxDz) return false;
  return dist(a.position, b.position) <= hop;
};

// Every linked pair, found through a grid of hop-sized cells (linear in the
// number of stations for a site, not quadratic).
export function linkedPairs(stations: TourStation[], link: StationLink, hop = AREA_HOP_M): Array<[number, number]> {
  const cell = (v: number) => Math.floor(v / hop);
  const grid = new Map<string, number[]>();
  stations.forEach((s, i) => {
    const k = `${cell(s.position.x)},${cell(s.position.y)}`;
    const list = grid.get(k);
    if (list) list.push(i); else grid.set(k, [i]);
  });
  const pairs: Array<[number, number]> = [];
  stations.forEach((s, i) => {
    const cx = cell(s.position.x), cy = cell(s.position.y);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const j of grid.get(`${cx + dx},${cy + dy}`) ?? []) {
          if (j > i && link(s, stations[j])) pairs.push([i, j]);
        }
      }
    }
  });
  return pairs;
}

export function groupStations(stations: TourStation[], link: StationLink, hop = AREA_HOP_M): TourStation[][] {
  const parent = stations.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
    return i;
  };
  for (const [i, j] of linkedPairs(stations, link, hop)) {
    const a = find(i), b = find(j);
    if (a !== b) parent[a] = b;
  }
  const groups = new Map<number, TourStation[]>();
  stations.forEach((s, i) => {
    const r = find(i);
    const g = groups.get(r);
    if (g) g.push(s); else groups.set(r, [s]);
  });
  return [...groups.values()];
}

const byScan = (a: TourStation, b: TourStation) =>
  (a.fileId === b.fileId ? a.index - b.index : a.fileId < b.fileId ? -1 : 1);

function centreOf(stations: TourStation[]): Vec3 {
  const c = { x: 0, y: 0, z: 0 };
  for (const s of stations) { c.x += s.position.x; c.y += s.position.y; c.z += s.position.z; }
  return { x: c.x / stations.length, y: c.y / stations.length, z: c.z / stations.length };
}

export function nearestStation(stations: TourStation[], p: Vec3): TourStation {
  let best = stations[0], bestD = Infinity;
  for (const s of stations) {
    const d = dist(s.position, p);
    if (d < bestD) { bestD = d; best = s; }
  }
  return best;
}

// Storeys more than this apart (in the floor heights under the scans).
export const FLOOR_GAP_M = 1.8;

// Storey of each scan, 0 = the lowest: floor heights sorted, a new storey
// wherever the next one is more than FLOOR_GAP_M higher.
export function floorLevels(stations: TourStation[], floorZ: (s: TourStation) => number): { levelOf: Map<string, number>; count: number } {
  const sorted = stations.map((s) => ({ id: s.id, z: floorZ(s) })).sort((a, b) => a.z - b.z);
  const levelOf = new Map<string, number>();
  let level = 0;
  sorted.forEach((it, i) => {
    if (i > 0 && it.z - sorted[i - 1].z > FLOOR_GAP_M) level++;
    levelOf.set(it.id, level);
  });
  return { levelOf, count: sorted.length ? level + 1 : 0 };
}

export interface TourAreaOptions {
  link: StationLink;
  hop?: number;
  // A file's name without its extension.
  fileName: (fileId: string) => string;
  names?: TourAreaName[];
  mode?: TourAreaMode;
  groups?: TourAreaGroup[];
  // Height of the floor under a scan (the floor mode's storeys).
  floorZ?: (s: TourStation) => number;
}

type Part =
  | { kind: 'gaps'; stations: TourStation[] }
  | { kind: 'file'; stations: TourStation[]; fileId: string; piece: number }
  | { kind: 'floor'; stations: TourStation[]; level: number; piece: number }
  | { kind: 'group'; stations: TourStation[]; group: TourAreaGroup };

// Split by gaps, largest piece first.
const pieces = (stations: TourStation[], opts: TourAreaOptions) =>
  groupStations(stations, opts.link, opts.hop).sort((a, b) => b.length - a.length || (a[0].id < b[0].id ? -1 : 1));

function partsOf(stations: TourStation[], opts: TourAreaOptions): Part[] {
  const mode = opts.mode ?? 'gaps';
  if (mode === 'file') {
    const byFile = new Map<string, TourStation[]>();
    for (const s of stations) {
      const list = byFile.get(s.fileId);
      if (list) list.push(s); else byFile.set(s.fileId, [s]);
    }
    return [...byFile].flatMap(([fileId, list]) => pieces(list, opts).map((p, i) => ({ kind: 'file' as const, stations: p, fileId, piece: i + 1 })));
  }
  if (mode === 'floor') {
    const { levelOf } = floorLevels(stations, opts.floorZ ?? ((s) => s.position.z));
    const byLevel = new Map<number, TourStation[]>();
    for (const s of stations) {
      const l = levelOf.get(s.id) ?? 0;
      const list = byLevel.get(l);
      if (list) list.push(s); else byLevel.set(l, [s]);
    }
    return [...byLevel].flatMap(([level, list]) => pieces(list, opts).map((p, i) => ({ kind: 'floor' as const, stations: p, level, piece: i + 1 })));
  }
  if (mode === 'custom') {
    const byId = new Map(stations.map((s) => [s.id, s]));
    const taken = new Set<string>();
    const out: Part[] = [];
    for (const group of opts.groups ?? []) {
      const own = group.stationIds.filter((id) => byId.has(id) && !taken.has(id)).map((id) => byId.get(id)!);
      own.forEach((s) => taken.add(s.id));
      if (own.length) out.push({ kind: 'group', stations: own, group });
    }
    const rest = stations.filter((s) => !taken.has(s.id));
    return [...out, ...groupStations(rest, opts.link, opts.hop).map((g) => ({ kind: 'gaps' as const, stations: g }))];
  }
  return groupStations(stations, opts.link, opts.hop).map((g) => ({ kind: 'gaps' as const, stations: g }));
}

// Largest area first. A drawn area keeps its own name; otherwise a name
// someone saved for this way of splitting wins (the earliest one when an area
// holds several); otherwise the automatic one: the file's or the floor's, and
// for gap areas the file's for the area holding most of its scans, the rest
// "Area 2", "Area 3"… by size.
export function tourAreas(stations: TourStation[], opts: TourAreaOptions): TourArea[] {
  if (stations.length === 0) return [];
  const mode = opts.mode ?? 'gaps';
  const parts = partsOf(stations, opts)
    .map((p) => ({ ...p, stations: p.stations.slice().sort(byScan) }))
    .sort((a, b) => b.stations.length - a.stations.length || (a.stations[0].id < b.stations[0].id ? -1 : 1));

  // The part holding most of a file's scans is that file's home; only a gap
  // area takes the file's name from it (drawn areas count, so a piece left
  // over beside a drawn main house is not named after the house's file).
  const homeOf = new Map<string, number>();
  const bestCount = new Map<string, number>();
  const mainFile = new Map<number, string>();
  parts.forEach((p, pi) => {
    const counts = new Map<string, number>();
    for (const s of p.stations) counts.set(s.fileId, (counts.get(s.fileId) ?? 0) + 1);
    let main = p.stations[0].fileId;
    for (const [fid, n] of counts) {
      if (n > (counts.get(main) ?? 0)) main = fid;
      if (n > (bestCount.get(fid) ?? 0)) { bestCount.set(fid, n); homeOf.set(fid, pi); }
    }
    mainFile.set(pi, main);
  });

  const saved = (opts.names ?? []).filter((n) => (n.mode ?? 'gaps') === mode);
  const autoName = (p: Part, pi: number): string => {
    const fallback = `Area ${pi + 1}`;
    if (p.kind === 'file') {
      const name = opts.fileName(p.fileId).trim() || fallback;
      return p.piece > 1 ? `${name}, part ${p.piece}` : name;
    }
    if (p.kind === 'floor') return p.piece > 1 ? `Floor ${p.level + 1}, part ${p.piece}` : `Floor ${p.level + 1}`;
    const main = mainFile.get(pi)!;
    const fileName = opts.fileName(main).trim();
    return homeOf.get(main) === pi && fileName ? fileName : fallback;
  };

  return parts.map((p, pi) => {
    const centre = centreOf(p.stations);
    const representative = nearestStation(p.stations, centre);
    const ids = new Set(p.stations.map((s) => s.id));
    const own = p.kind === 'group' ? null : saved.find((n) => ids.has(n.stationId) && n.name.trim());
    const name = p.kind === 'group' ? p.group.name : own ? own.name.trim() : autoName(p, pi);
    return {
      key: p.kind === 'group' ? `g:${p.group.id}` : p.stations[0].id,
      stations: p.stations,
      centre,
      name,
      named: p.kind === 'group' || !!own,
      nameStation: own ? own.stationId : representative.id,
      representative,
      colour: AREA_COLOURS[pi] ?? AREA_OTHER_COLOUR,
      ...(p.kind === 'group' ? { groupId: p.group.id } : {}),
    };
  });
}

export function areaByStation(areas: TourArea[]): Map<string, TourArea> {
  const map = new Map<string, TourArea>();
  for (const a of areas) for (const s of a.stations) map.set(s.id, a);
  return map;
}

// Where an area is from a point: its nearest scan, how far that is on the
// ground, and the compass bearing to it. The scene's +Y is north unless the
// site was placed on the map with another heading (northDeg = the compass
// heading of +Y).
export interface AreaHeading { station: TourStation; distance: number; bearing: number }
export function headingToArea(area: TourArea, from: Vec3, northDeg = 0): AreaHeading {
  const station = nearestStation(area.stations, from);
  return { station, distance: flatDist(station.position, from), bearing: compassBearing(from, station.position, northDeg) };
}

export function compassBearing(from: Vec3, to: Vec3, northDeg = 0): number {
  const sceneAngle = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
  return wrap360(northDeg + 90 - sceneAngle);
}

// A world heading (radians, atan2 of the scene's x/y, what the viewer posts)
// as a compass bearing.
export const bearingOfHeading = (heading: number, northDeg = 0) => wrap360(northDeg + 90 - (heading * 180) / Math.PI);

export const wrap360 = (deg: number) => ((deg % 360) + 360) % 360;
// -180 … 180: how far to turn from `from` to face `to`, right positive.
export const turnDeg = (from: number, to: number) => {
  const d = wrap360(to - from);
  return d > 180 ? d - 360 : d;
};

const WORDS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];
export const compassWord = (bearing: number) => WORDS[Math.round(wrap360(bearing) / 45) % 8];

export function formatAreaDistance(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  if (m >= 100) return `${Math.round(m / 5) * 5} m`;
  return `${Math.max(1, Math.round(m))} m`;
}
